import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { type ActionItem, type ICardFilter } from 'shared/ui'
import { TableStore } from 'shared/ui/TableNew'
import { RequestLoadData } from 'shared/api/lib'
import { CalendarFilterStore } from 'shared/ui/CalendarDropdown/store/CalendarFilterStore'
import { CalendarFilterEnum } from 'shared/ui/CalendarDropdown/calendarEnum'
import { totalsToPercents } from 'shared/lib/totalsToPercents'
import { uiStore } from 'shared/store/uiStore'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { tableLimitStore } from 'entities/TableLimit'
import { ReviewContactRow, ReviewsApi } from 'entities/Review'
import {
  EnumReviewAccount,
  type IResponseReview,
  type IReviewAccountType,
  type IReviewContactsFilter,
  type IReviewContactsParams,
  type IReviewExportParams,
} from 'entities/Review/api/types'
import type { IOpenContactsDetails } from 'widgets/ContactsDetails'
import { ContactsDetailsManageStore } from 'widgets/ContactsDetails/store'
import { ContactsTagsModalStore } from 'widgets/ContactsTagsModal'
import { ExportAnalyticsModalStore, getExportReportAction } from 'widgets/ExportAnalyticsModal'
import { getReviewActions } from 'pages/reviews/actions/getReviewActions'

export class ReviewAnalyticsStore {
  readonly calendarFilterStore: CalendarFilterStore
  readonly tableStore: TableStore<ReviewContactRow>
  readonly contactsTagsModalStore: ContactsTagsModalStore
  private readonly _review: IResponseReview
  private _accountsTypes: Set<IReviewAccountType> = new Set()
  private _activeFilter: IReviewContactsFilter = 'total'
  private _search = ''
  private _itemsMap = new Map<number, ReviewContactRow>()
  private _disposeParamsWithoutPage: IReactionDisposer | null = null
  private _disposeParamsGetItems: IReactionDisposer | null = null
  private readonly _contactsDetailsManageStore: ContactsDetailsManageStore
  private readonly _exportModalStore: ExportAnalyticsModalStore<IReviewExportParams>

  constructor(
    review: IResponseReview,
    private _entities: IEntitiesStore,
    private _onNavigateBack: () => void
  ) {
    this._review = review
    this.calendarFilterStore = new CalendarFilterStore(review.created_at)
    this.calendarFilterStore.setSelectedCalendarFilter(CalendarFilterEnum.allTime)
    this.calendarFilterStore.setParamsDate()
    this.tableStore = new TableStore<ReviewContactRow>({
      key: 'review-contacts',
      element: 'contact',
      page: 1,
      limit: 10,
      sortOrder: 'asc',
      getLimit: () => tableLimitStore.getLimit('review-contacts-table'),
      setLimit: (value) => tableLimitStore.setLimit('review-contacts-table', value),
    })

    this.contactsTagsModalStore = new ContactsTagsModalStore(this._entities)
    this._contactsDetailsManageStore = new ContactsDetailsManageStore(this._entities)
    this._exportModalStore = new ExportAnalyticsModalStore<IReviewExportParams>((params) =>
      ReviewsApi.exportReviewById(this._review.id, params)
    )

    this.setAccountsTypes()
    makeAutoObservable(this)
  }

  init = () => {
    this._contactsDetailsManageStore.init()
    void this.loadData()
    this._reactionParamsWithoutPage()
    this._reactionParamsGetItems()
  }

  private _requestLoadData = new RequestLoadData({
    requestCallback: async ({ cancelRequest }) => {
      runInAction(() => {
        this.tableStore.setLoading(true)
      })

      const { data } = await ReviewsApi.getReviewContactsById(
        this._review.id,
        this.paramsGetItems,
        { cancelToken: cancelRequest.token }
      )

      runInAction(() => {
        this._itemsMap.clear()
        data.data.forEach((item) => {
          if (item.id) {
            this._itemsMap.set(item.id, new ReviewContactRow(item))
          }
        })

        this.tableStore.setTotal(data.total)
      })
    },
    finallyCallback: () => {
      runInAction(() => {
        this.tableStore.setLoading(false)
      })
    },
  })

  private _reactionParamsWithoutPage = () => {
    this._disposeParamsWithoutPage?.()
    this._disposeParamsWithoutPage = reaction(
      () => this.paramsWithoutPage,
      () => {
        runInAction(() => {
          this.tableStore.setLoading(true)
          this.tableStore.setPage(1)
        })
      }
    )
  }

  private _reactionParamsGetItems = () => {
    this._disposeParamsGetItems?.()
    this._disposeParamsGetItems = reaction(
      () => this.paramsGetItems,
      () => this._requestLoadData.debounceLoadData()
    )
  }

  loadData = async () => {
    return this._requestLoadData.loadData()
  }

  setSearch = (value: string) => {
    this._search = value
  }

  onSuccessDelete = () => {
    this.tableStore.unselectAllIds()
    void this.loadData()
  }

  onSuccessManageTags = () => {
    void this.loadData()
  }

  setActiveFilter = (filter: IReviewContactsFilter) => {
    this._activeFilter = filter
    this.tableStore.resetSelected()
  }

  setAccountsTypes() {
    this._accountsTypes.clear()

    this._review.accounts.forEach((account) => {
      this._accountsTypes.add(account.type)
    })
  }

  isReviewAccountType = (key: string): key is IReviewAccountType => key in EnumReviewAccount

  resetPaginationPage = () => {
    this.tableStore.setPage(1)
    this.tableStore.setLimit(this.cachedLimit)
  }

  private _openExportModal = () => {
    this._exportModalStore.onOpenExport({ timezone: uiStore.timezone })
  }

  dispose = () => {
    this.resetPaginationPage()
    this._disposeParamsWithoutPage?.()
    this._disposeParamsGetItems?.()
    this._requestLoadData.dispose()
    this._contactsDetailsManageStore.reset()
    this._exportModalStore.dispose()
  }

  get cachedLimit() {
    return tableLimitStore.getLimit('review-contacts-table') ?? 10
  }

  get name() {
    return this._review.name
  }

  get statistics() {
    return this._review.all_statistics
  }

  get rating() {
    return this._review.all_statistics?.rating ?? 0
  }

  get activeFilter() {
    return this._activeFilter
  }

  get search() {
    return this._search
  }

  get items() {
    return Array.from(this._itemsMap.values())
  }

  get selectedContactIds() {
    const contactIds = this.tableStore.selectedRows
      .map((row) => row.contactId)
      .filter((contactId): contactId is number => contactId !== null)

    return Array.from(new Set(contactIds))
  }

  get onOpenContactsDetails(): IOpenContactsDetails {
    return {
      disabled: this._contactsDetailsManageStore.disabled,
      open: (data) => {
        void this._contactsDetailsManageStore.onOpenContactDetails(
          {
            contactId: data.contactId,
          },
          'review'
        )
      },
    }
  }

  get contactsDetailsStore() {
    return this._contactsDetailsManageStore.contactsDetailsStore
  }

  get statBlocks(): ICardFilter<IReviewContactsFilter>[] {
    const stats = this.statistics

    if (!stats) return []

    const [happyPct, unhappyPct] = totalsToPercents(
      [stats.happy_count, stats.unhappy_count],
      stats.total_count
    )

    const validLinks = Object.entries(stats.links ?? {}).filter(
      (entry): entry is [IReviewAccountType, number] => {
        const [key, count] = entry
        return this.isReviewAccountType(key) && (this._accountsTypes.has(key) || count > 0)
      }
    )

    const linkPercentages = totalsToPercents(
      validLinks.map(([, count]) => count),
      stats.links_total
    )

    return [
      {
        key: 'total',
        title: 'Total ratings',
        amount: stats.total_count,
        tooltipProps: {
          width: 200,
          placement: 'top',
          breakWord: true,
          label: "Each contact's latest rating, anonymous and repeat ratings aren't counted",
        },
      },
      {
        key: 'happy',
        title: 'Stars (4-5)',
        amount: stats.happy_count,
        percentage: happyPct,
        tooltipProps: {
          width: 140,
          placement: 'top',
          breakWord: true,
          label: 'Contacts who rated you 4 or 5 stars',
        },
      },
      {
        key: 'unhappy',
        title: 'Stars (1-3)',
        amount: stats.unhappy_count,
        percentage: unhappyPct,
        tooltipProps: {
          width: 140,
          placement: 'top',
          breakWord: true,
          label: 'Contacts who rated you 1 to 3 stars',
        },
      },
      {
        key: 'clicked',
        title: 'Review page clicks',
        amount: stats.click_count,
        tooltipProps: {
          width: 200,
          placement: 'top',
          breakWord: true,
          label: 'All clicks to open your review page, including anonymous and repeat clicks',
        },
      },
      ...validLinks.map(
        ([key, count], i): ICardFilter<IReviewContactsFilter> => ({
          key,
          title: `${EnumReviewAccount[key]} clicks`,
          amount: count,
          percentage: linkPercentages[i],
          tooltipProps: {
            placement: 'top',
            breakWord: true,
            label: `Clicks to leave a ${EnumReviewAccount[key]} review`,
          },
        })
      ),
    ]
  }

  private get _isExportDisabled() {
    return !this.statistics?.total_count
  }

  private get _exportAction(): ActionItem {
    return getExportReportAction({
      disabled: this._isExportDisabled,
      onExport: this._openExportModal,
    })
  }

  get actions() {
    return getReviewActions({
      item: { id: this._review.id, shortUrl: this._review.short_url },
      entities: this._entities,
      onSuccessDelete: this._onNavigateBack,
      exportAction: this._exportAction,
    })
  }

  get paramsWithoutPage(): Omit<IReviewContactsParams, 'page'> {
    return {
      filter: this._activeFilter,
      search: this._search.trim(),
      length: this.tableStore.limit,
      sortBy: this.tableStore.sortBy || undefined,
      order: this.tableStore.sortOrder,
    }
  }

  get paramsGetItems(): IReviewContactsParams {
    return {
      page: this.tableStore.page,
      ...this.paramsWithoutPage,
    }
  }
}
